import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import InternalTransferService from '../services/InternalTransferService';

const initialState = {
    datas: [],
    details: null,
    pagination: {
        page: 0,
        per_page: 10,
        total: 0,
        totalPages: 0,
    },
    grand_total: 0,
    error: null,
    loading: false,
    submitLoading: false,
};

export const fetchInternalTransfers = createAsyncThunk(
    'internalTransfer/all',
    async (params) => {
        try {
            const resp = await InternalTransferService.All(params);
            return resp;
        } catch (e) {
            return e.response;
        }
    },
);

export const createInternalTransfer = createAsyncThunk(
    'internalTransfer/create',
    async (data) => {
        try {
            const response = await InternalTransferService.Create(data);
            return response;
        } catch (error) {
            return error.response;
        }
    },
);

export const fetchInternalTransferDetails = createAsyncThunk(
    'internalTransfer/details',
    async (id) => {
        try {
            const response = await InternalTransferService.Get(id);
            return response;
        } catch (error) {
            return error.response;
        }
    },
);

export const updateInternalTransfer = createAsyncThunk(
    'internalTransfer/update',
    async ({ id, data }) => {
        try {
            const response = await InternalTransferService.Update(id, data);
            return response;
        } catch (error) {
            return error.response;
        }
    },
);

export const deleteInternalTransfer = createAsyncThunk(
    'internalTransfer/delete',
    async (id) => {
        try {
            const response = await InternalTransferService.Delete(id);
            return response;
        } catch (error) {
            return error.response;
        }
    },
);

export const cancelInternalTransfer = createAsyncThunk(
    'internalTransfer/cancel',
    async (id) => {
        try {
            const response = await InternalTransferService.Cancel(id);
            return response;
        } catch (error) {
            return error.response;
        }
    },
);

export const confirmInternalTransfer = createAsyncThunk(
    'internalTransfer/confirm',
    async (id) => {
        try {
            const response = await InternalTransferService.Confirm(id);
            return response;
        } catch (error) {
            return error.response;
        }
    },
);

export const completeInternalTransfer = createAsyncThunk(
    'internalTransfer/transfer',
    async (id) => {
        try {
            const response = await InternalTransferService.Transfer(id);
            return response;
        } catch (error) {
            return error.response;
        }
    },
);

const internalTransferSlice = createSlice({
    name: 'internalTransfer',
    initialState,
    reducers: {
        resetInternalTransferDetails: (state) => {
            state.details = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchInternalTransfers.pending, (state) => {
                state.loading = true;
            })
            .addCase(fetchInternalTransfers.fulfilled, (state, action) => {
                if (action.payload?.status === 200) {
                    state.datas = action.payload?.data?.data?.internalTransfers || [];
                    state.pagination = action.payload?.data?.data?.pagination || initialState.pagination;
                    state.grand_total = action.payload?.data?.data?.grand_total || 0;
                } else {
                    state.error = action.payload?.data?.message;
                    toast.error(state.error);
                }
                state.loading = false;
            })
            .addCase(fetchInternalTransfers.rejected, (state, action) => {
                state.error = action.payload?.data;
                state.loading = false;
            })
            // Create
            .addCase(createInternalTransfer.pending, (state) => {
                state.submitLoading = true;
            })
            .addCase(createInternalTransfer.fulfilled, (state, action) => {
                if (action.payload?.status === 200) {
                    // toast.success(action.payload?.data?.message);
                } else {
                    toast.error(action.payload?.data?.message || 'Something went wrong');
                }
                state.submitLoading = false;
            })
            .addCase(createInternalTransfer.rejected, (state) => {
                state.submitLoading = false;
            })
            // Details
            .addCase(fetchInternalTransferDetails.pending, (state) => {
                state.loading = true;
            })
            .addCase(fetchInternalTransferDetails.fulfilled, (state, action) => {
                if (action.payload?.status === 200) {
                    state.details = action.payload?.data?.data;
                } else {
                    toast.error(action.payload?.data?.message);
                }
                state.loading = false;
            })
            // Update
            .addCase(updateInternalTransfer.pending, (state) => {
                state.submitLoading = true;
            })
            .addCase(updateInternalTransfer.fulfilled, (state, action) => {
                if (action.payload?.status === 200) {
                    // toast.success(action.payload?.data?.message);
                } else {
                    toast.error(action.payload?.data?.message || 'Something went wrong');
                }
                state.submitLoading = false;
            })
            // Delete
            .addCase(deleteInternalTransfer.fulfilled, (state, action) => {
                if (action.payload?.status === 200) {
                    toast.success(action.payload?.data?.message);
                } else {
                    toast.error(action.payload?.data?.message);
                }
            })
            .addCase(cancelInternalTransfer.pending, (state) => {
                state.submitLoading = true;
            })
            .addCase(cancelInternalTransfer.fulfilled, (state, action) => {
                state.submitLoading = false;
                if (action.payload?.status === 200) {
                    if (action.payload?.data?.data) {
                        state.details = action.payload.data.data;
                    }
                } else {
                    toast.error(action.payload?.data?.message);
                }
            })
            .addCase(cancelInternalTransfer.rejected, (state) => {
                state.submitLoading = false;
            })
            .addCase(confirmInternalTransfer.pending, (state) => {
                state.submitLoading = true;
            })
            .addCase(confirmInternalTransfer.fulfilled, (state, action) => {
                state.submitLoading = false;
                if (action.payload?.status === 200 && action.payload?.data?.data) {
                    state.details = action.payload.data.data;
                } else if (action.payload?.status !== 200) {
                    toast.error(action.payload?.data?.message);
                }
            })
            .addCase(confirmInternalTransfer.rejected, (state) => {
                state.submitLoading = false;
            })
            .addCase(completeInternalTransfer.pending, (state) => {
                state.submitLoading = true;
            })
            .addCase(completeInternalTransfer.fulfilled, (state, action) => {
                state.submitLoading = false;
                if (action.payload?.status === 200 && action.payload?.data?.data) {
                    state.details = action.payload.data.data;
                } else if (action.payload?.status !== 200) {
                    toast.error(action.payload?.data?.message);
                }
            })
            .addCase(completeInternalTransfer.rejected, (state) => {
                state.submitLoading = false;
            });
    },
});

export const { resetInternalTransferDetails } = internalTransferSlice.actions;

export const internalTransferSelector = (state) => state?.internalTransfer;

export default internalTransferSlice.reducer;
